import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Archive, RotateCcw, Layers } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { mpApi } from "./mpShared";

const SWATCHES = ["#6366f1", "#0ea5e9", "#8b5cf6", "#ec4899", "#f59e0b", "#14b8a6", "#ef4444", "#22c55e", "#f97316", "#06b6d4", "#a855f7", "#84cc16"];

/** Add, rename, recolour and archive the services the planner tracks. */
export default function ServicesDialog({ open, onClose, businessId, onChanged }) {
  const [list, setList] = useState(null);
  const [name, setName] = useState("");
  const [color, setColor] = useState(SWATCHES[6]);
  const [busy, setBusy] = useState("");
  const [showArchived, setShowArchived] = useState(false);

  const load = useCallback(() => mpApi("/services", { params: { business_id: businessId || undefined } }).then((d) => setList(d.services)).catch((e) => toast.error(e.message)), [businessId]);
  useEffect(() => { if (open) { load(); setName(""); } }, [open, load]);

  const add = async () => {
    const label = name.trim();
    if (!label) return toast.error("Enter a service name");
    setBusy("add");
    try {
      await mpApi("/services", { method: "POST", body: { label, color, business_id: businessId || undefined } });
      toast.success(`"${label}" added — set its monthly targets in Service Targets`);
      setName(""); setColor(SWATCHES[(SWATCHES.indexOf(color) + 1) % SWATCHES.length]);
      await load(); onChanged?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };
  const update = async (s, patch, msg) => {
    setBusy(s.key);
    try { await mpApi(`/services/${s.key}`, { method: "PUT", body: { ...patch, business_id: businessId || undefined } }); if (msg) toast.success(msg); await load(); onChanged?.(); }
    catch (e) { toast.error(e.message); load(); } finally { setBusy(""); }
  };

  const active = (list || []).filter((s) => s.active);
  const archived = (list || []).filter((s) => !s.active);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-xl overflow-y-auto rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600"><Layers className="h-4 w-4 text-white" /></span>Services</DialogTitle>
          <p className="pl-10 text-sm text-slate-500">The types of deliverable you plan and track each month.</p>
        </DialogHeader>

        {/* Add */}
        <div className="rounded-xl border border-indigo-200 bg-indigo-50/50 p-3">
          <label className="text-xs font-semibold text-slate-700">Add a service</label>
          <div className="mt-1.5 flex gap-2">
            <input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} autoFocus data-testid="mp-service-name"
              placeholder="e.g. Podcast, Website Update, Photo Shoot" maxLength={40} className="h-10 flex-1 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900" />
            <Button onClick={add} disabled={busy === "add"} data-testid="mp-service-add" className="h-10 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700">
              {busy === "add" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1 h-4 w-4" />Add</>}
            </Button>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] text-slate-500">Colour</span>
            {SWATCHES.map((c) => (
              <button key={c} type="button" onClick={() => setColor(c)} title={c} className={`h-6 w-6 rounded-full ring-offset-2 transition ${color === c ? "ring-2 ring-slate-900" : "hover:scale-110"}`} style={{ backgroundColor: c }} />
            ))}
          </div>
        </div>

        {/* List */}
        <div className="space-y-1.5">
          {list === null ? <div className="py-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></div>
            : active.map((s) => (
              <div key={s.key} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-2.5 py-2">
                <input type="color" defaultValue={s.color} title="Colour" onBlur={(e) => e.target.value !== s.color && update(s, { color: e.target.value })}
                  className="h-8 w-9 shrink-0 cursor-pointer rounded border border-slate-200" />
                <input defaultValue={s.label} maxLength={40} onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== s.label) update(s, { label: v }, `Renamed to "${v}"`); }}
                  onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                  className="min-w-0 flex-1 rounded-lg border border-transparent px-2 py-1.5 text-sm font-medium text-slate-900 hover:border-slate-200 focus:border-indigo-300 focus:outline-none" />
                <span className="shrink-0 text-[11px] text-slate-400">{s.task_count} task{s.task_count === 1 ? "" : "s"}</span>
                <button onClick={() => { if (window.confirm(`Archive "${s.label}"? It disappears from targets and the dashboard. Its ${s.task_count} task(s) are kept and can be re-tagged. You can restore it later.`)) update(s, { active: false }, `"${s.label}" archived`); }}
                  disabled={busy === s.key} title="Archive" className="shrink-0 rounded-lg p-2 text-slate-400 hover:bg-rose-50 hover:text-rose-600">
                  {busy === s.key ? <Loader2 className="h-4 w-4 animate-spin" /> : <Archive className="h-4 w-4" />}
                </button>
              </div>
            ))}
        </div>

        {archived.length > 0 && (
          <div>
            <button onClick={() => setShowArchived((v) => !v)} className="text-xs font-medium text-slate-500 hover:text-slate-700">{showArchived ? "Hide" : "Show"} {archived.length} archived service{archived.length === 1 ? "" : "s"}</button>
            {showArchived && (
              <div className="mt-1.5 space-y-1.5">
                {archived.map((s) => (
                  <div key={s.key} className="flex items-center gap-2 rounded-xl border border-dashed border-slate-200 px-3 py-2 text-sm text-slate-500">
                    <span className="h-3 w-3 rounded-full" style={{ backgroundColor: s.color }} />
                    <span className="flex-1">{s.label}</span>
                    <span className="text-[11px]">{s.task_count} tasks</span>
                    <button onClick={() => update(s, { active: true }, `"${s.label}" restored`)} className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-indigo-600 hover:bg-indigo-50"><RotateCcw className="h-3.5 w-3.5" /> Restore</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between border-t border-slate-100 pt-3">
          <span className="text-xs text-slate-400">Rename or recolour by editing and clicking away. Archiving never deletes tasks.</span>
          <Button variant="outline" onClick={onClose} className="rounded-lg">Done</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
